import { UserRole, UserInterest } from './types';

/**
 * Mapping configuration for user interests.
 * Each interest maps to catalog tags, tech radar quadrants, Q&A topics,
 * and template categories for personalization.
 */
export interface InterestMapping {
  /** Tags for catalog entity filtering */
  tags: string[];
  /** Tech radar quadrants relevant to this interest */
  techRadarQuadrants: string[];
  /** Q&A (Qeta) tags for this interest */
  qetaTags: string[];
  /** Template categories for scaffolder recommendations */
  templateCategories: string[];
}

/**
 * Mapping configuration for user roles.
 * Each role maps to preferred template types and tags.
 */
export interface RoleMapping {
  /** Preferred template types for this role */
  preferredTypes: string[];
  /** Tags relevant to this role */
  tags: string[];
}

/**
 * Maps user interests to catalog tags, tech radar quadrants, and Q&A topics.
 * Used by PersonalizationContext to personalize content recommendations.
 */
export const INTEREST_MAPPINGS: Record<UserInterest, InterestMapping> = {
  apis: {
    tags: ['api', 'rest', 'graphql', 'openapi', 'swagger'],
    techRadarQuadrants: ['languages-and-frameworks', 'tools'],
    qetaTags: ['api', 'rest', 'graphql'],
    templateCategories: ['api', 'service'],
  },
  microservices: {
    tags: ['microservice', 'service', 'backend', 'distributed'],
    techRadarQuadrants: ['languages-and-frameworks', 'platforms'],
    qetaTags: ['microservices', 'architecture'],
    templateCategories: ['service', 'backend'],
  },
  'cloud-infrastructure': {
    tags: ['aws', 'azure', 'gcp', 'cloud', 'infrastructure', 'terraform'],
    techRadarQuadrants: ['platforms', 'tools'],
    qetaTags: ['cloud', 'aws', 'azure', 'infrastructure'],
    templateCategories: ['infrastructure', 'cloud'],
  },
  'data-engineering': {
    tags: ['data', 'pipeline', 'etl', 'analytics', 'databricks', 'spark'],
    techRadarQuadrants: ['data-management', 'tools'],
    qetaTags: ['data', 'pipeline', 'analytics'],
    templateCategories: ['data', 'pipeline'],
  },
  'machine-learning': {
    tags: ['ml', 'ai', 'machine-learning', 'model', 'training'],
    techRadarQuadrants: ['data-management', 'languages-and-frameworks'],
    qetaTags: ['ml', 'ai', 'machine-learning'],
    templateCategories: ['ml', 'ai'],
  },
  security: {
    tags: ['security', 'auth', 'oauth', 'secrets', 'compliance'],
    techRadarQuadrants: ['tools', 'techniques'],
    qetaTags: ['security', 'authentication', 'compliance'],
    templateCategories: ['security'],
  },
  'ci-cd': {
    tags: ['ci', 'cd', 'pipeline', 'jenkins', 'github-actions', 'deployment'],
    techRadarQuadrants: ['tools', 'techniques'],
    qetaTags: ['ci-cd', 'deployment', 'pipeline'],
    templateCategories: ['cicd', 'pipeline'],
  },
  documentation: {
    tags: ['docs', 'documentation', 'techdocs', 'mkdocs'],
    techRadarQuadrants: ['tools'],
    qetaTags: ['documentation', 'techdocs'],
    templateCategories: ['documentation'],
  },
  innersource: {
    tags: ['innersource', 'open-source', 'contribution', 'bazaar'],
    techRadarQuadrants: ['techniques'],
    qetaTags: ['innersource', 'contribution'],
    templateCategories: ['library', 'plugin'],
  },
  testing: {
    tags: ['testing', 'test', 'jest', 'playwright', 'e2e', 'unit-test'],
    techRadarQuadrants: ['tools', 'techniques'],
    qetaTags: ['testing', 'quality'],
    templateCategories: ['testing'],
  },
  observability: {
    tags: ['monitoring', 'logging', 'tracing', 'metrics', 'datadog', 'grafana'],
    techRadarQuadrants: ['tools', 'platforms'],
    qetaTags: ['observability', 'monitoring', 'logging'],
    templateCategories: ['observability'],
  },
  kubernetes: {
    tags: ['kubernetes', 'k8s', 'container', 'helm', 'eks', 'aks'],
    techRadarQuadrants: ['platforms', 'tools'],
    qetaTags: ['kubernetes', 'containers'],
    templateCategories: ['kubernetes', 'container'],
  },
  serverless: {
    tags: ['serverless', 'lambda', 'function', 'faas'],
    techRadarQuadrants: ['platforms', 'techniques'],
    qetaTags: ['serverless', 'lambda'],
    templateCategories: ['serverless', 'function'],
  },
};

/**
 * Maps user roles to relevant template types and tags.
 * Used by PersonalizationContext to customize scaffolder recommendations.
 */
export const ROLE_TEMPLATE_MAPPINGS: Record<UserRole, RoleMapping> = {
  developer: {
    preferredTypes: ['service', 'website', 'library'],
    tags: ['backend', 'frontend', 'api'],
  },
  'devops-engineer': {
    preferredTypes: ['pipeline', 'infrastructure'],
    tags: ['ci-cd', 'deployment', 'automation'],
  },
  'platform-engineer': {
    preferredTypes: ['infrastructure', 'plugin', 'template'],
    tags: ['platform', 'backstage', 'tooling'],
  },
  'tech-lead': {
    preferredTypes: ['service', 'documentation', 'adr'],
    tags: ['architecture', 'documentation'],
  },
  architect: {
    preferredTypes: ['documentation', 'adr', 'system'],
    tags: ['architecture', 'design', 'system'],
  },
  'data-engineer': {
    preferredTypes: ['pipeline', 'data'],
    tags: ['data', 'pipeline', 'etl', 'analytics'],
  },
  sre: {
    preferredTypes: ['infrastructure', 'monitoring'],
    tags: ['observability', 'reliability', 'monitoring'],
  },
  manager: {
    preferredTypes: ['documentation', 'website'],
    tags: ['documentation', 'team'],
  },
  other: {
    preferredTypes: [],
    tags: [],
  },
};
